import React from 'react';
import {
  Card, Button, Textarea, Checkbox, Badge, Alert, Spinner, useToast,
} from '@parsina/framework';
import { Mic, Square, Sparkles } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useCreditPrices } from '../../lib/useCreditPrices.js';
import { isOutOfCredits } from '../../lib/bffClient.js';
import { HISTORY_SECTIONS, SECTION_IDS, nextSortOrder, groupBySection } from '../../lib/history.js';
import { CostChip, BalanceLine } from '../common/ParsehCost.jsx';
import { AiAnswerNote } from '../common/PracticeNotice.jsx';

// ورود صوتی — dictate a case, then confirm what it heard.
//
// ── Speech-to-text happens in the BROWSER. Audio never leaves it. ────────
//
// `parseCaseVoice` takes a `String transcript`, and that is not an optimisation:
// the BFF's JSON body limit is 1 MB, so a base64 recording would not fit and
// would fail as a 413 BEFORE any resolver ran — i.e. with no error message this
// app controls. The Web Speech API does the recognition here and hands back text.
//
// A browser without it is not blocked: the transcript box below is a plain
// textarea, and typing or pasting into it reaches exactly the same mutation. That
// is also the escape hatch when the recogniser mishears — which is why the
// normalized transcript comes BACK in the result and is shown.
//
// ── The AI never writes to the case ──────────────────────────────────────
//
// `parseCaseVoice` returns a DRAFT and stores nothing. The student ticks what
// they keep and submits it through `addCaseHistory` with `source: 'voice'`, which
// is the same call every hand-typed entry goes through. There is no state in
// which an entry belongs to the AI rather than to the student — a student who
// cannot tell which sentences a model put in their mouth cannot study from them.
//
// The demographics the transcript mentioned are shown and NOT applied. Writing a
// name, an age and a sex onto the case behind the student's back would be the AI
// editing the record, and `assistant.voice.draft.demographicsNote` says where to
// change them instead.

const SPEECH_LANG = 'fa-IR';

/** The browser's recogniser, or null. Prefixed on WebKit; absent on Firefox. */
function speechRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

const sectionLabelKey = (id) => HISTORY_SECTIONS.find((s) => s.id === id)?.labelKey || null;

export default function VoicePanel({ entries, assistant, addHistory, balance }) {
  const { t } = useTranslation();
  const toast = useToast();

  const [transcript, setTranscript] = React.useState('');
  const [listening, setListening] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [keep, setKeep] = React.useState({}); // index → boolean
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState('');

  const recognizerRef = React.useRef(null);
  const supported = Boolean(speechRecognition());
  const parsing = assistant.busy === 'voice';
  // The policy's price once the list has arrived; the constant (0 today) until then.
  const cost = useCreditPrices().costOf('VOICE');

  // Stop the microphone if the panel unmounts mid-sentence. Without this the
  // recogniser keeps the mic open after the student navigates away, which the
  // browser shows as a permanently recording tab.
  React.useEffect(
    () => () => {
      try {
        recognizerRef.current?.stop();
      } catch {
        /* already stopped */
      }
    },
    [],
  );

  const startListening = () => {
    const Recognition = speechRecognition();
    if (!Recognition) return;

    const recognizer = new Recognition();
    recognizer.lang = SPEECH_LANG;
    // Interim results are shown as they arrive, because a student dictating a
    // whole history needs to see that the microphone is working long before the
    // final result lands.
    recognizer.interimResults = true;
    recognizer.continuous = true;

    let committed = transcript;
    recognizer.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) committed = `${committed} ${chunk}`.trim();
        else interim += chunk;
      }
      setTranscript(interim ? `${committed} ${interim}`.trim() : committed);
    };
    recognizer.onerror = (event) => {
      console.warn('[voice] recognition error:', event?.error);
      setListening(false);
    };
    recognizer.onend = () => setListening(false);

    recognizerRef.current = recognizer;
    setProblem('');
    setListening(true);
    recognizer.start();
  };

  const stopListening = () => {
    try {
      recognizerRef.current?.stop();
    } catch {
      /* already stopped */
    }
    setListening(false);
  };

  const parse = async () => {
    const text = transcript.trim();
    if (!text) {
      setProblem('assistant.voice.needsTranscript');
      return;
    }
    setProblem('');
    stopListening();
    try {
      const result = await assistant.parseVoice(text);
      setDraft(result || null);
      // Everything it found is ticked to begin with: the student is reviewing
      // what they just said, and having to tick each line back on is a review
      // nobody does carefully.
      setKeep(Object.fromEntries((result?.entries || []).map((_, i) => [i, true])));
    } catch (err) {
      if (isOutOfCredits(err)) {
        toast.error(t('errors.insufficientCredits'));
        return;
      }
      console.error('[voice] parse failed:', err?.message);
      toast.error(t('errors.generic'));
    }
  };

  const confirm = async () => {
    const rows = (draft?.entries || [])
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry, index }) =>
        // An eighth section value is dropped rather than sent. The set is
        // validated on write and REJECTED, and `addCaseHistory` is
        // all-or-nothing — so one unknown section would fail the whole
        // submission, including the six entries the student did want.
        keep[index] && SECTION_IDS.includes(entry?.section),
      );

    if (!rows.length) return;

    setSaving(true);
    try {
      const grouped = groupBySection(entries);
      // A running sort order per section, so several drafted entries landing in
      // the same section keep the order they were dictated in.
      const cursor = {};
      const payload = rows.map(({ entry }) => {
        const section = entry.section;
        if (cursor[section] == null) cursor[section] = nextSortOrder(grouped[section]);
        else cursor[section] += 1;
        return {
          section,
          label: null,
          body: String(entry.body || '').trim(),
          structured: entry.structured ?? null,
          // PROVENANCE, set once and never updatable: this entry came from a
          // voice draft the student confirmed.
          source: 'voice',
          sortOrder: cursor[section],
          recordedAtMs: null,
        };
      });

      await addHistory(payload);
      toast.success(t('assistant.voice.applied', { count: payload.length }));
      setDraft(null);
      setKeep({});
      setTranscript('');
    } catch (err) {
      console.error('[voice] confirm failed:', err?.message);
      toast.error(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const demographics = draft
    ? [
        draft.displayName,
        draft.ageDisplay || (draft.ageYears != null ? String(draft.ageYears) : ''),
        draft.sex,
        draft.chiefComplaint,
      ].filter(Boolean)
    : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
      <Card style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
            {t('assistant.voice.title')}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, lineHeight: 1.9, color: C.textMuted }}>
            {t('assistant.voice.body')}
          </p>
        </div>

        {!supported && <Alert variant="warning">{t('assistant.voice.unsupported')}</Alert>}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {supported && (
            <Button
              variant={listening ? 'destructive' : 'default'}
              onClick={listening ? stopListening : startListening}
              disabled={parsing}
              data-testid="voice-toggle"
            >
              {/* NOT mirrored by `--dir-flip`: a microphone has no reading
                  direction, and mirroring one by accident is the classic RTL bug. */}
              {listening ? <Square size={15} aria-hidden /> : <Mic size={16} aria-hidden />}
              {listening ? t('assistant.voice.stop') : t('assistant.voice.start')}
            </Button>
          )}
          {listening && (
            <span style={{ fontSize: 12, fontWeight: 700, color: C.red }}>
              {t('assistant.voice.listening')}
            </span>
          )}
        </div>

        <Textarea
          label={t('assistant.voice.transcript')}
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder={t('assistant.voice.transcriptPlaceholder')}
          rows={6}
          disabled={parsing}
          maxLength={20000}
          inputStyle={{ fontSize: '1rem' }}
          data-testid="voice-transcript"
        />

        {problem && (
          <p style={{ margin: 0, fontSize: 11.5, color: C.red, fontWeight: 600 }}>{t(problem)}</p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button onClick={parse} disabled={parsing} data-testid="voice-parse">
            {parsing ? <Spinner size={14} color="#fff" /> : <Sparkles size={16} aria-hidden />}
            {parsing ? t('assistant.voice.parsing') : t('assistant.voice.parse')}
          </Button>
          {/* Zero TODAY, and «رایگان» is what the chip prints for it. That is a
              fact about the service (the RPC calls neither the credit check nor
              the deduction), not a policy — when it starts charging, the number in
              src/constants/pricing.js changes and this follows. */}
          <CostChip cost={cost} before />
          <BalanceLine balance={balance} />
        </div>
      </Card>

      {draft && (
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Alert variant="info" title={t('assistant.voice.draft.title')}>
            {t('assistant.voice.draft.body')}
          </Alert>

          {/* THE NORMALIZED TRANSCRIPT THE PARSE ACTUALLY RAN ON — Persian and
              Arabic-Indic digits folded to ASCII. Shown so a student can see what
              the recogniser heard when the parse comes back wrong, which is the
              only way to tell a mis-hearing from a mis-parse. */}
          {draft.transcript && draft.transcript !== transcript && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 4 }}>
                {t('assistant.voice.transcript')}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.95,
                  color: CT.ink2,
                  background: CT.bg,
                  borderRadius: 10,
                  padding: 10,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {draft.transcript}
              </div>
            </div>
          )}

          {demographics.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.textPrimary }}>
                {t('assistant.voice.draft.demographics')}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0' }}>
                {demographics.map((value) => (
                  <Badge key={value} variant="outline">
                    {value}
                  </Badge>
                ))}
              </div>
              {/* NOT APPLIED. Writing these onto the case would be the AI editing
                  the record — the one thing it never does. */}
              <p style={{ margin: 0, fontSize: 11, color: C.textMuted }}>
                {t('assistant.voice.draft.demographicsNote')}
              </p>
            </div>
          )}

          {draft.entries?.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {draft.entries.map((entry, index) => {
                const labelKey = sectionLabelKey(entry.section);
                return (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${entry.section}-${index}`}
                    style={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      padding: 10,
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Checkbox
                      checked={!!keep[index]}
                      onChange={(next) => setKeep((prev) => ({ ...prev, [index]: !!next }))}
                      // The whole row is not the label: the body can be a
                      // paragraph, and a checkbox whose hit area is a paragraph
                      // is one that gets toggled while a student is selecting
                      // text to read it again.
                      label={null}
                      disabled={saving}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Badge variant={labelKey ? 'outline' : 'destructive'}>
                        {/* An unknown section is shown RAW and is dropped on
                            submit — the seven are a closed set validated on
                            write, and `addCaseHistory` is all-or-nothing, so
                            sending an eighth would fail the whole batch. */}
                        {labelKey ? t(labelKey) : entry.section}
                      </Badge>
                      <div
                        style={{
                          fontSize: 13,
                          lineHeight: 2,
                          color: C.textPrimary,
                          marginTop: 6,
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {entry.body}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 12.5, color: C.textMuted }}>
              {t('assistant.voice.draft.none')}
            </p>
          )}

          <AiAnswerNote />

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              onClick={confirm}
              disabled={saving || !Object.values(keep).some(Boolean)}
              data-testid="voice-confirm"
            >
              {saving ? <Spinner size={14} color="#fff" /> : null}
              {saving ? t('assistant.voice.draft.confirming') : t('assistant.voice.draft.confirm')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDraft(null);
                setKeep({});
              }}
              disabled={saving}
            >
              {t('assistant.voice.draft.discard')}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
